<template>
  <div class="body-5 article-content" v-html="content" :class="categorySlug"></div>
</template>

<script>
  export default {
    name: 'ArticleContent',
    props: ['content', 'categorySlug']
  }
</script>

<style lang="scss">
  .article-content {
    section {
      @apply bg-white;
      margin-bottom: 75px;

      &#materials {
          &::before {
            content: '';
            width: 0;
            height: 0;
            border-top: 58px solid transparent;
            border-right: 1140px solid #F0F0F0;
          }

          &::after {
            content: '';
            width: 0;
            height: 0;
            border-bottom: 15px solid transparent;
            border-right: 1140px solid #F0F0F0;
          }

          background-color: #f0f0f0;
      }
      
      &#preparation {
        .column-row {
          margin-top: 0px;
        }

        &::before {
          content: '';
          width: 0;
          height: 0;
          border-top: 18px solid transparent;
          border-right: 1140px solid #F0F0F0;
        }

        &::after {
          content: '';
          width: 0;
          height: 0;
          border-bottom: 35px solid transparent;
          border-right: 1140px solid #F0F0F0;
        }
        
        background-color: #f0f0f0;
      }
      
      &#instructions {
        .stage {
          @apply pt-10;

          .step {
            background-color: transparent;
            margin-top: 0;
            margin-left: 0;
            margin-right: 0;

            &:nth-child(2n) {
              background-color: transparent;
              &::before {
                content: none;
              }

              &::after {
                content: none;
              }
            }
          }
        }

        .stage,
        .step {
          position: relative;
          z-index: 1;
          @apply -mx-3 -my-4;

          &:nth-child(2n) {
            background-color: #f0f0f0;
            z-index: 0;

            div.col-5 {
              z-index: 1;
            }

            &::before {
              content: '';
              width: 0;
              height: 0;
              position: absolute;
              top: -58px;
              left: 0;
              border-top: 58px solid transparent;
              border-right: 1140px solid #F0F0F0;
            }

            &::after {
              content: '';
              width: 0;
              height: 0;
              position: absolute;
              bottom: -80px;
              left: 0;
              border-bottom: 80px solid transparent;
              border-right: 1140px solid #F0F0F0;
            }
          }
        }
      }

      &#conclusion {
        width: 1140px;
        padding-top: 75px;
        padding-bottom: 75px;
        margin-bottom: 75px;

        & > div.solid-background-wrapper {

          & > div.solid-background {
            clip-path: polygon(70px 0, 1065px 15px, 1050px 100%, 85px 98%);
            padding: 30px 0;
          }
        }

        .column-row {
          margin-top: 20px;

          div {
            column-count: 2;
            column-gap: 30px;
          }

          p {
            @apply text-white;
          }
        }
      }
    }

    figure {
      display: table;
      filter: drop-shadow(5px 5px #C0C0C0);

      img {
        width: 100%;
      }
    }

    figcaption {
      font-family: "Source Sans Pro", sans-serif;
      font-weight: 600;
      font-size: 18px;
      @apply p-5 w-full;
      display: table-caption;
      caption-side: bottom;

      p {
        @apply text-white;
      }

      &::after {
        content: '';
        width: 0;
        height: 0;
        position: absolute;
        bottom: -15px;
        left: 0;
        border-bottom: 15px solid transparent;
        border-left: 445px solid lightslategrey;
      }
    }

    figcaption.image-intro {
      font-family: "Source Sans Pro", sans-serif;
      font-weight: bold;
      font-size: 22px;
      color: white;
      background-color: lightslategrey;
    }

    table {
      th {
        @apply font-bold text-left;
      }

      thead {
        th, td {
          @apply font-bold text-left;
        }
      }

      td, th {
        @apply pr-3;
      }
    }

    h6 {
      @apply font-heading-primary uppercase;
      font-size: 24px;
    }

    .section-header {
      &.primary {
        @apply uppercase text-white leading-none flex items-center mb-9;
        background-color: lightslategrey;
        padding: 15px 0 5px 95px;
        font-size: 52px;
      }

      &.secondary {
        height: 60px;
        align-items: center;
        margin: 40px auto;
        justify-content: center;
        font-size: 30px;
        @apply text-white uppercase flex;

        @media (min-width: config('screens.lg')) {
          width: 920px;
        }
      }

      &.intro {
        width: 625px;
        height: 120px;
        padding-left: 110px;
        @apply text-white items-center text-center flex;
      }

      &.materials {
        width: 650px;
        height: 215px;
        padding-left: 110px;
        @apply text-white items-center flex;
      }

      &.instructions {
        width: 516px;
        height: 124px;
        padding-left: 110px;
        @apply text-white items-center flex;
      }

      &.conclusion {
        padding-left: 110px;
        @apply text-white flex;
      }

      &.credits {
        width: 200px;
        height: 94px;
        margin-left: 90px;
        padding-left: 20px;
        @apply text-white items-center flex;
      }
    }

    .step {
      h5 {
        width: 156px;
        height: 66px;
        margin-left: -20px;
        padding-left: 20px;
        @apply items-center flex;
      }
    }

    .stage {
      h5 {
        width: 174px;
        height: 70px;
        margin-left: -20px;
        padding-left: 20px;
        @apply items-center flex;
      }
    }

    .warning {
      @apply mb-2;

      .warning-image {
        width: 42px;
        height: 90px;
        background: center no-repeat url('~@/assets/images/proj-help.svg');
      }
    }

    .serves {
      .serves-image {
        width: 95px;
        height: 78px;
      }

      h5 {
        margin: 0;
        padding: 0;
      }

      p {
        margin: 0;
        padding: 0;
      }
    }

    .time {
      .time-image {
        width: 78px;
        height: 78px;
      }

      h5 {
        margin: 0;
        padding: 0;
      }

      p {
        margin: 0;
        padding: 0;
      }
    }

    .section-intro {
      @apply font-heading-primary font-bold uppercase;
      font-size: 24px;

      .glossary-term {
        @apply uppercase font-heading-primary;
        font-size: 24px;
      }
    }

    .glossary-term {
      @apply text-black;
      outline: none;
    }

    .column-row {
      @apply flex mt-9;

      > div {
        @apply px-3;
        flex-basis: 100%;

        &:only-child {
          @apply w-full;
        }

        &:first-child:not(:last-child) {
          flex: 1 0 50%;

          @media (min-width: config('screens.xl')) {
            margin-left: 110px;
            flex: 1 0 550px;
          }
        }

        & > :first-child {
          @apply mt-0;
        }
      }
    }

    .sidebar-rule {
      padding-top: 38px;

      p {
        @apply uppercase;
        font-size: 24px;
      }

      .glossary-term {
        font-size: inherit;
        font-family: inherit;
      }

      &::before {
        content: '';
        height: 8px;
        width: 160px;
        position: absolute;
        margin-top: -38px;
        background-color: lightslategrey;
        border-radius: 25px;
      }
    }

    .sidebar-quote {
      padding-top: 79px;

      p {
        font-size: 24px;
        text-transform: uppercase;
      }

      &::before {
        content: '';
        width: 65px;
        height: 49px;
        position: absolute;
        margin-top: -79px;
      }
    }

    cite.sidebar-1 {
      font-size: 18px;
      color: #808080;
    }

    .sidebar-boxed-wrapper {
      filter: drop-shadow(5px 5px #C0C0C0);

      .sidebar-boxed,
      .sidebar-boxed-2 {
        @apply py-6 px-6 mb-3 -mb-3;
        background-color: lightslategrey;

        p {
          @apply text-white;

          &:last-child {
            @apply mb-0;
          }
        }
      }

      .sidebar-boxed {
        clip-path: polygon(15px 15px, 450px 0, 435px 100%, 0 98%);
      }

      .sidebar-boxed-2 {
        clip-path: polygon(0 0, 450px 15px, 435px 98%, 15px 100%);
      }
    }

    p {
      @apply mb-4;

      .glossary-term {
        @apply font-primary font-bold relative;
        font-size: 20px;
        cursor: default;

        &:before,
        &:after {
          content: ' ';
          @apply font-primary font-normal text-white bg-lerner-blue absolute border-lerner-blue normal-case;
          font-size: 20px;
          opacity: 0;
        }

        &:hover:before, &:hover:after,
        &:focus:before, &:focus:after {
          @apply border-lerner-blue;
          opacity: 1;
          transition: opacity 0.2s ease-in-out;
        }

        &:hover:before,
        &:focus:before {
          content: ' ';
          @apply text-lerner-blue bg-transparent;
          border-left: 10px solid transparent;
          border-right: 10px solid transparent;
          border-top: 10px solid;
          width: 0;
          height: 0;
          transform: translateX(-50%);
          left: 50%;
        }

        &:hover:after,
        &:focus:after {
          content: attr(data-title);
          padding: 2px 5px;
          border-radius: 4px;
          transform: translateX(-50%);
          bottom: 100%;
          min-width: 500px;
          text-align: center;
        }
      }
    }

    &.craft {
      h4,
      h5,
      h6,
      h7,
      p.section-intro,
      p.body-1 {
        @apply text-c1-g;
      }

      .section-header.primary {
        @apply bg-c1-b;
      }

      .section-header.intro {
        background: center no-repeat url('~@/assets/images/craft-category-proj-header-bar.svg');
      }

      .section-header.materials {
        background: center no-repeat url('~@/assets/images/craft-category-proj-need-bar.svg');
      }

      .section-header.instructions {
        background: center no-repeat url('~@/assets/images/craft-category-proj-how-bar.svg');
      }

      .section-header.credits {
        background: center no-repeat url('~@/assets/images/craft-category-proj-cred.svg');
      }

      section#conclusion {
        background: center repeat-y url('~@/assets/images/craft-category-proj-side-horiz.svg');
        & > div.solid-background-wrapper {
          filter: drop-shadow(5px 5px #E2801E);

          & > div.solid-background {
            background-color: #F8A14C;
          }
        }
      }

      .step {
        h5 {
          background: center no-repeat url('~@/assets/images/craft-category-proj-step.svg');
        }
      }

      .stage {
        h5 {
          background: center no-repeat url('~@/assets/images/craft-category-proj-stage.svg');
        }
      }

      .serves {
        .serves-image {
          background: center no-repeat url('~@/assets/images/craft-category-proj-serv.svg');
        }
      }

      .time {
        .time-image {
          background: center no-repeat url('~@/assets/images/craft-category-proj-time.svg');
        }
      }

      .sidebar-1 p {
        @apply text-c1-g;
      }

      .sidebar-rule {
        &::before {
          @apply bg-c1-b;
        }
      }

      .sidebar-boxed {
        @apply bg-c1-e;
      }

      figcaption {
        @apply bg-c1-e;
        
        &::after {
          border-left-color: #F6891F;
        }
      }
    }

    &.build {
      h4,
      h5,
      h6,
      h7,
      p.section-intro,
      p.body-1 {
        @apply text-c2-g;
      }

      .section-header.primary {
        @apply bg-c2-b;
      }

      .section-header.intro {
        background: center no-repeat url('~@/assets/images/build-category-proj-header-bar.svg');
      }

      .section-header.materials {
        background: center no-repeat url('~@/assets/images/build-category-proj-need-bar.svg');
      }

      .section-header.instructions {
        background: center no-repeat url('~@/assets/images/build-category-proj-how-bar.svg');
      }

      .section-header.credits {
        background: center no-repeat url('~@/assets/images/build-category-proj-cred.svg');
      }

      section#conclusion {
        background: center repeat-y url('~@/assets/images/build-category-proj-side-horiz.svg');
        & > div.solid-background-wrapper {
          filter: drop-shadow(5px 5px #223983);

          & > div.solid-background {
            background-color: #4E619C;
          }
        }
      }

      .step {
        h5 {
          background: center no-repeat url('~@/assets/images/build-category-proj-step.svg');
        }
      }

      .stage {
        h5 {
          margin-left: 80px;
          background: center no-repeat url('~@/assets/images/build-category-proj-stage.svg');
        }
      }

      .serves {
        .serves-image {
          background: center no-repeat url('~@/assets/images/build-category-proj-serv.svg');
        }
      }

      .time {
        .time-image {
          background: center no-repeat url('~@/assets/images/build-category-proj-time.svg');
        }
      }

      .sidebar-1 p {
        @apply text-c2-g;
      }

      .sidebar-rule {
        &::before {
          @apply bg-c2-b;
        }
      }

      .sidebar-boxed {
        @apply bg-c2-e;
      }

      figcaption {
        @apply bg-c2-e;
        
        &::after {
          border-left-color: #223983;
        }
      }
    }

    &.experiment {
      h4,
      h5,
      h6,
      h7,
      p.section-intro,
      p.body-1 {
        @apply text-c3-g;
      }

      .section-header.primary {
        @apply bg-c3-b;
      }

      .section-header.intro {
        background: center no-repeat url('~@/assets/images/experiment-category-proj-header-bar.svg');
        height: 140px;
      }

      .section-header.materials {
        background: center no-repeat url('~@/assets/images/experiment-category-proj-need-bar.svg');
      }

      .section-header.instructions {
        background: center no-repeat url('~@/assets/images/experiment-category-proj-how-bar.svg');
      }

      .section-header.credits {
        background: center no-repeat url('~@/assets/images/experiment-category-proj-cred.svg');
      }

      section#conclusion {
        background: center repeat-y url('~@/assets/images/experiment-category-proj-side-horiz.svg');
        & > div.solid-background-wrapper {
          filter: drop-shadow(5px 5px #ECA713);

          & > div.solid-background {
            background-color: #FDC450;
          }
        }
      }

      .step {
        h5 {
          background: center no-repeat url('~@/assets/images/experiment-category-proj-step.svg');
        }
      }

      .stage {
        h5 {
          background: center no-repeat url('~@/assets/images/experiment-category-proj-stage.svg');
        }
      }

      .serves {
        .serves-image {
          background: center no-repeat url('~@/assets/images/experiment-category-proj-serv.svg');
        }
      }

      .time {
        .time-image {
          background: center no-repeat url('~@/assets/images/experiment-category-proj-time.svg');
        }
      }

      .sidebar-1 p {
        @apply text-c3-g;
      }

      .sidebar-rule {
        &::before {
          @apply bg-c3-b;
        }
      }

      .sidebar-boxed {
        @apply bg-c3-e;
      }

      figcaption {
        @apply bg-c3-e;

        &::after {
          border-left-color: #FCB016;
        }
      }
    }

    &.eat {
      h4,
      h5,
      h6,
      h7,
      p.section-intro,
      p.body-1 {
        @apply text-c4-g;
      }

      .section-header.primary {
        @apply bg-c4-b;
      }

      .section-header.intro {
        background: center no-repeat url('~@/assets/images/eat-category-proj-header-bar.svg');
      }

      .section-header.materials {
        background: center no-repeat url('~@/assets/images/eat-category-proj-need-bar.svg');
      }

      .section-header.instructions {
        background: center no-repeat url('~@/assets/images/eat-category-proj-how-bar.svg');
      }

      .section-header.credits {
        background: center no-repeat url('~@/assets/images/eat-category-proj-cred.svg');
      }

      section#conclusion {
        background: center repeat-y url('~@/assets/images/eat-category-proj-side-horiz.svg');
        & > div.solid-background-wrapper {
          filter: drop-shadow(5px 5px #6AB542);

          & > div.solid-background {
            background-color: #90CE73;
          }
        }
      }

      .step {
        h5 {
          background: center no-repeat url('~@/assets/images/eat-category-proj-step.svg');
        }
      }

      .stage {
        h5 {
          background: center no-repeat url('~@/assets/images/eat-category-proj-stage.svg');
        }
      }

      .serves {
        .serves-image {
          background: center no-repeat url('~@/assets/images/eat-category-proj-serv.svg');
        }
      }

      .time {
        .time-image {
          background: center no-repeat url('~@/assets/images/eat-category-proj-time.svg');
        }
      }

      .sidebar-1 p {
        @apply text-c4-g;
      }

      .sidebar-rule {
        &::before {
          @apply bg-c4-b;
        }
      }

      .sidebar-boxed {
        @apply bg-c4-e;
      }

      figcaption {
        @apply bg-c4-e;

        &::after {
          border-left-color: #6CBE45;
        }
      }
    }

    &.celebrate {
      h4,
      h5,
      h6,
      h7,
      p.section-intro,
      p.body-1 {
        @apply text-c5-g;
      }

      .section-header.primary {
        @apply bg-c5-b;
      }

      .section-header.intro {
        background: center no-repeat url('~@/assets/images/celebrate-category-proj-header-bar.svg');
      }

      .section-header.materials {
        background: center no-repeat url('~@/assets/images/celebrate-category-proj-need-bar.svg');
      }

      .section-header.instructions {
        background: center no-repeat url('~@/assets/images/celebrate-category-proj-how-bar.svg');
      }

      .section-header.credits {
        background: center no-repeat url('~@/assets/images/celebrate-category-proj-cred.svg');
      }

      section#conclusion {
        background: center repeat-y url('~@/assets/images/celebrate-category-proj-side-horiz.svg');
        & > div.solid-background-wrapper {
          filter: drop-shadow(5px 5px #DE2F40);
          & > div.solid-background {
            background-color: #F1555B;
          }
        }
      }

      .step {
        h5 {
          background: center no-repeat url('~@/assets/images/celebrate-category-proj-step.svg');
        }
      }

      .stage {
        h5 {
          background: center no-repeat url('~@/assets/images/celebrate-category-proj-stage.svg');
        }
      }

      .serves {
        .serves-image {
          background: center no-repeat url('~@/assets/images/celebrate-category-proj-serv.svg');
        }
      }

      .time {
        .time-image {
          background: center no-repeat url('~@/assets/images/celebrate-category-proj-time.svg');
        }
      }

      .sidebar-1 p {
        @apply text-c5-g;
      }

      .sidebar-rule {
        &::before {
          @apply bg-c5-b;
        }
      }

      .sidebar-boxed {
        @apply bg-c5-e;
      }

      figcaption {
        @apply bg-c5-e;

        &::after {
          border-left-color: #ED1C24;
        }
      }
    }

    &.default {
      p.section-intro {
        @apply text-grey-1;
      }

      .section-header.primary {
        @apply bg-grey-1;
      }

      .sidebar-1 p {
        @apply text-grey-1;
      }

      .sidebar-quote {

        &::before {
          fill: config('colors.grey-1');
        }
      }

      .sidebar-rule {
        &::before {
          @apply bg-grey-1;
        }
      }

      .sidebar-boxed {
        @apply bg-grey-1;
      }

      figcaption {
        @apply bg-grey-1;

        &::after {
          border-left-color: lightslategrey;
        }
      }
    }
  }
</style>
